import { extractTemplateVariables, resolveTemplate } from "@/lib/utils/crm/template-variables";
import { getAutoValue } from "./variable-helpers";
import type { Contact, TicketSummary } from "@/lib/types/database";

// ============================================================================
// VARIABLE RESOLVERS
// ============================================================================

export function getVariableValue(
  varName: string,
  savedValues: Record<string, string>,
  selectedClient?: any
): string {
  // First check if we have a saved value
  const savedValue = savedValues[varName];
  if (savedValue && savedValue.trim() !== "") {
    return savedValue;
  }

  // Fall back to auto-filled value
  return getAutoValue(varName, selectedClient);
}

export function hasValue(
  varName: string,
  savedValues: Record<string, string>,
  selectedClient?: any
): boolean {
  const value = getVariableValue(varName, savedValues, selectedClient);
  return Boolean(value && value.trim() !== "");
}

export function getAllVariables(subject: string, body: string): string[] {
  return [...new Set([...extractTemplateVariables(subject), ...extractTemplateVariables(body)])];
}

export function getFormattedValues(savedValues: Record<string, string>): Record<string, string> {
  return savedValues;
}

export function replaceVariablesInText(
  text: string,
  savedValues: Record<string, string>,
  selectedClient?: any
): string {
  let result = text;

  // Detect if text is HTML (contains HTML tags)
  const isHtml = /<\/?[a-z][\s\S]*>/i.test(text);

  // First replace with saved values
  Object.entries(savedValues).forEach(([key, value]) => {
    if (value && value.trim() !== "") {
      let processedValue = String(value);
      // First, convert literal "\n" strings (backslash + n) to actual newline characters
      processedValue = processedValue.replace(/\\n/g, "\n");
      // Then convert \n to line breaks based on format
      if (isHtml) {
        processedValue = processedValue.replace(/\n/g, "<br>");
      }
      const regex = new RegExp(`\\{\\{${key}\\}\\}`, "g");
      result = result.replace(regex, processedValue);
    }
  });

  // Then replace any remaining variables with auto values
  if (selectedClient) {
    const remainingVariables = extractTemplateVariables(result);
    remainingVariables.forEach((varName) => {
      const autoValue = getAutoValue(varName, selectedClient);
      if (autoValue) {
        let processedValue = String(autoValue);
        // First, convert literal "\n" strings (backslash + n) to actual newline characters
        processedValue = processedValue.replace(/\\n/g, "\n");
        // Then convert \n to line breaks based on format
        if (isHtml) {
          processedValue = processedValue.replace(/\n/g, "<br>");
        }
        const regex = new RegExp(`\\{\\{${varName}\\}\\}`, "g");
        result = result.replace(regex, processedValue);
      }
    });
  }

  return result;
}

export function resolveTemplateUnified(
  subject: string,
  body: string,
  savedValues: Record<string, string>,
  contact?: Contact,
  ticket?: TicketSummary
) {
  const result = resolveTemplate(subject, body, {
    contact,
    ticket,
    manualValues: savedValues,
  });

  // Debug logging removed to prevent infinite re-renders

  return result;
}

export function hasUnfilledVariables(
  subject: string,
  body: string,
  savedValues: Record<string, string>,
  contact?: Contact,
  ticket?: TicketSummary
): boolean {
  const result = resolveTemplateUnified(subject, body, savedValues, contact, ticket);
  return result.unfilledVariables.length > 0;
}
